import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Text,
  VStack,
  Radio,
  RadioGroup,
  Portal,
  Image,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useLanguage } from "../../contexts/LanguageContext";

const HeaderLanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { selectedLanguage, changeLanguage } = useLanguage();

  const languages = [
    {
      code: "en",
      name: "English",
      nativeName: "English",
      flag: "/flag/us.png",
    },
    {
      code: "zh",
      name: "Chinese",
      nativeName: "中文",
      flag: "/flag/china.png",
    },
    {
      code: "ja",
      name: "Japanese",
      nativeName: "日本語",
      flag: "/flag/japan.png",
    },
    {
      code: "ko",
      name: "Korean",
      nativeName: "한국어",
      flag: "/flag/korea.png",
    },
  ];

  const handleLanguageSelect = (languageName) => {
    changeLanguage(languageName);
    setIsOpen(false);
  };

  const getDisplayText = () => {
    const selected = languages.find(lang => lang.name === selectedLanguage);
    return selected ? selected.nativeName : selectedLanguage;
  };

  const getDisplayFlag = () => {
    const selected = languages.find(lang => lang.name === selectedLanguage);
    return selected ? selected.flag : "/png/flags/default.png";
  };

  return (
    <Menu 
      isOpen={isOpen} 
      onClose={() => setIsOpen(false)}
      placement="bottom-end"
      strategy="fixed"
    >
      <MenuButton
        as={Button}
        variant="solid"
        bg="#FFFFFF"
        backdropFilter="blur(10px)"
        border="1px solid rgba(255, 255, 255, 0.2)"
        color="#000B8C"
        _hover={{ 
          bg: "#F7F9FC",
          borderColor: "#000B8C",
          transform: "translateY(-2px)",
        }}
        _active={{ 
          bg: "#E2E8F0",
        }}
        onClick={() => setIsOpen(!isOpen)}
        rightIcon={
          <ChevronDownIcon 
            transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
            transition="transform 0.2s"
            color="#000B8C"
          />
        }
        size={{ base: "sm", md: "md" }}
        boxShadow="0 8px 32px rgba(0, 0, 0, 0.1)"
        borderRadius="xl"
        px={{ base: 2, md: 4 }}
        py={{ base: 1, md: 2 }}
        minW={{ base: "100px", sm: "120px", md: "140px" }}
        maxW={{ base: "140px", md: "none" }}
        transition="all 0.3s"
        fontWeight="semibold"
      >
        <HStack spacing={{ base: 1, md: 2 }}>
          <Image 
            src={getDisplayFlag()} 
            alt={selectedLanguage + " flag"}
            w={{ base: "16px", md: "20px" }} 
            h={{ base: "12px", md: "15px" }} 
            borderRadius="sm"
            fallbackSrc="/png/flags/default.png"
            objectFit="cover"
          />
          <Text fontSize={{ base: "xs", md: "sm" }} noOfLines={1}>
            {getDisplayText()}
          </Text>
        </HStack>
      </MenuButton>
      
      <Portal>
        <MenuList
          bg="white"
          borderColor="gray.200"
          boxShadow="2xl"
          minW={{ base: "280px", sm: "300px", md: "320px" }}
          maxW={{ base: "90vw", md: "400px" }}
          p={0}
          borderRadius="xl"
          border="1px solid"
          mt={2}
          overflow="hidden"
          zIndex={1500}
        >
          <RadioGroup value={selectedLanguage} onChange={handleLanguageSelect}>
            <VStack spacing={0} align="stretch">
              {languages.map((language, index) => {
                const isSelected = selectedLanguage === language.name;
                
                return (
                  <MenuItem
                    key={language.code}
                    onClick={() => handleLanguageSelect(language.name)}
                    bg={isSelected ? "#F0F4FF" : "white"}
                    _hover={{ 
                      bg: isSelected ? "#F0F4FF" : "#F7F9FC",
                    }}
                    _focus={{ 
                      bg: isSelected ? "#F0F4FF" : "#F7F9FC"
                    }}
                    borderRadius="none"
                    border="1px solid"
                    borderColor={isSelected ? "#4F7EE3" : "#E5E7EB"}
                    borderTop={index === 0 ? "1px solid" : "none"}
                    borderTopColor={index === 0 ? (isSelected ? "#4F7EE3" : "#E5E7EB") : "transparent"}
                    p={{ base: 3, md: 4 }}
                    m={0}
                    transition="all 0.2s"
                    cursor="pointer"
                  >
                    <HStack spacing={{ base: 3, md: 4 }} w="100%" justify="space-between">
                      <HStack spacing={{ base: 2, md: 3 }}>
                        <Image 
                          src={language.flag} 
                          alt={language.name + " flag"}
                          w={{ base: "20px", md: "24px" }} 
                          h={{ base: "15px", md: "18px" }} 
                          borderRadius="sm"
                          fallbackSrc="/png/flags/default.png"
                          objectFit="cover"
                        />
                        <Text 
                          fontSize={{ base: "sm", md: "md" }} 
                          fontWeight="medium" 
                          color="gray.900"
                          noOfLines={1}
                        >
                          {language.nativeName}
                        </Text>
                      </HStack>
                      <Radio
                        value={language.name}
                        isChecked={isSelected}
                        colorScheme="blue"
                        size={{ base: "md", md: "lg" }}
                        onChange={() => handleLanguageSelect(language.name)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </HStack>
                  </MenuItem>
                );
              })}
            </VStack>
          </RadioGroup>
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default HeaderLanguageSelector;